/**
 * ManageBillingButton (Mar 2026) — opens the Stripe customer portal
 * (update card, view invoices, change/cancel plan). The backend endpoint
 * (`POST /api/billing/portal`) existed but had NO UI — Pro customers with
 * expired cards had no self-serve fix. Comped Pro accounts (founding /
 * lab testers) have no Stripe customer; the backend 400s and we explain.
 */
import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CreditCard } from "@phosphor-icons/react";
import { Capacitor } from "@capacitor/core";
import { openExternal } from "../lib/openExternal";

export const ManageBillingButton = ({ variant = "outline", size = "sm", className = "" }) => {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      // Native: link out to the default browser (Guideline 3.1.1 — same
      // rule as checkout). Web: same-tab so the portal's return URL works.
      if (Capacitor.isNativePlatform()) {
        await openExternal(data.url, { mode: "system" });
        setBusy(false);
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(
        detail === "No subscription on file"
          ? "This account doesn't have a Stripe subscription (complimentary Pro) — nothing to manage."
          : detail || "Couldn't open the billing portal — try again in a moment.",
      );
      setBusy(false);
    }
  };
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={open}
      disabled={busy}
      data-testid="manage-billing-btn"
      className={`gap-2 ${className}`}
    >
      <CreditCard weight="duotone" className="h-4 w-4" />
      {busy ? "Opening…" : "Manage billing"}
    </Button>
  );
};

export default ManageBillingButton;
